import { ChevronRight } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { PhaseConfig, defaultPhaseConfig, getAICreditSaleContent, getAIMiningMachineContent, getPINAIBannerContent, highlightAmountText, mapEventInfoToPhaseConfig, getCurrentPhaseTimeState, PhaseTimeState } from '../lib/phaseManager'
import { getEventInfo, formatNumberWithCommas, ApiError } from '../lib/api'
import { WalletConnect } from '../components/WalletConnect'
import { HomeTestPanel } from '../components/dev/HomeTestPanel'
import { DevConsole } from '../components/dev/DevConsole'
import { useBinanceCheck } from '../lib/useBinanceCheck'
import { useWallet } from '../lib/useWallet'
import { useToast } from '../lib/useToast'

// 扩展window类型以支持
declare global {
  interface Window {
    binancew3w?: any;
    isBinance?: boolean;
  }
}

// Import all images
import pianiBanner from '../assets/images/piani_banner.png'
import iconAllocation from '../assets/images/icon_allocation.png'
import iconCoin from '../assets/images/allocation/coin.png'
import pentacleGray from '../assets/images/pentacle_gray.png'
import pentacleLight from '../assets/images/pentacle_light.png'
import logoPinai from '../assets/images/logo/logo_pinai.png'
import logoGuide from '../assets/images/logo/logo_guide.png'
import logoTokenomics from '../assets/images/logo/logo_tokenomics.png'
import logoTwitter from '../assets/images/logo/logo_twitter.png'

export function HomePage() {
  const navigate = useNavigate()
  const [currentTime, setCurrentTime] = useState(new Date())
  const [phaseConfig, setPhaseConfig] = useState<PhaseConfig>(defaultPhaseConfig)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { checkBinanceAndExecute } = useBinanceCheck()
  const { address, isConnecting } = useWallet()
  const { showError } = useToast()
  

  // 加载Zendesk Widget脚本
  useEffect(() => {
    const script = document.createElement('script')
    script.id = 'ze-snippet'
    script.src = 'https://static.zdassets.com/ekr/snippet.js?key=32dcc327-74dd-4859-a6a0-0924017a6dbb'
    script.async = true
    
    
    document.head.appendChild(script)
    
    // 清理函数：组件卸载时移除脚本
    return () => {
      const existingScript = document.getElementById('ze-snippet')
      if (existingScript) {
        existingScript.remove()
      }
    }
  }, [])

  // 加载事件信息
  useEffect(() => {
    const loadEventInfo = async () => {
      const isBinanceApp = window.isBinance && window.binancew3w
      console.log('binancew3w detailed:', isBinanceApp, window.binancew3w)
      
      // 如果钱包正在连接，等待连接完成
      if (isConnecting) {
        return
      }
      
      // 只有在钱包连接完成但没有地址时才显示错误
      if (!address) {
        // showError("Please connect to binance wallet");
        return;
      }

      try {
        setIsLoading(true)
        setError(null)
        
        const eventInfo = await getEventInfo(address)
        const config = mapEventInfoToPhaseConfig(eventInfo)
        
        setPhaseConfig(config)
      } catch (err) {
        console.error('Failed to load event info:', err)
        if (err instanceof ApiError) {
          setError(`API错误: ${err.message}`)
        } else {
          setError('加载事件信息失败，使用默认配置')
        }
        // 出错时使用默认配置
        setPhaseConfig(defaultPhaseConfig)
      } finally {
        setIsLoading(false)
      }

    }

    loadEventInfo()
  }, [address, isConnecting, showError])

  // 更新当前时间
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date())
    }, 60000) // 每分钟更新一次
    return () => clearInterval(timer)
  }, [])

  // 从phaseManager获取各个组件的内容和样式
  const pinAIBanner = getPINAIBannerContent(phaseConfig, currentTime)
  const aiCreditSale = getAICreditSaleContent(phaseConfig, currentTime)
  const aiMiningMachine = getAIMiningMachineContent(phaseConfig, currentTime)

  // 高亮显示的文本
  const creditSaleHighlight = highlightAmountText(aiCreditSale.text)
  const miningMachineHighlight = highlightAmountText(aiMiningMachine.text)

  // 获取当前phase状态
  const currentPhaseState = getCurrentPhaseTimeState(phaseConfig, currentTime)
  
  // 判断哪个phase是活跃的
  const isPhase1Active = currentPhaseState === PhaseTimeState.DURING_PHASE1
  const isPhase2Active = currentPhaseState === PhaseTimeState.DURING_PHASE2

  // Phase1 卡片组件
  const Phase1Card = () => (
    <button 
      onClick={() => checkBinanceAndExecute(() => {
        // Phase1 的逻辑待实现
        console.log('Phase1 clicked')
        window.open('bnc://app.binance.com/mp/app?appId=xoqXxUSMRccLCrZNRebmzj&startPagePath=cGFnZXMvc3VicGFja2FnZXMvcHJlLWlkby9pbmRleA==&startPageQuery=aWQ9cHJlX3RnZV9pbmZyYQ==&showOptions=2')
      })}
      className={`relative w-full h-[161px] transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] ${
        isPhase1Active 
          ? 'gradient-border' 
          : 'rounded-[12px] border-[0.5px] border-solid border-[#CACFDB] bg-[rgba(255,255,255,0.75)]'
      }`}
    >
      <div className={isPhase1Active ? 'gradient-border-inner' : ''}>
        <div className="flex items-center gap-3 absolute top-[110px] left-[18px] right-[18px] min-h-[35px]">
            <img 
              src={iconAllocation} 
              alt="Allocation Icon" 
              className="w-7 h-7 rounded-lg flex-shrink-0 object-cover"
            />
                    <div className="flex-1 font-sf-pro font-small text-gray-900 text-sm sm:text-base tracking-[0] leading-[18px] sm:leading-[20px] break-words overflow-hidden flex items-center">
            Total Allocation {formatNumberWithCommas(phaseConfig.phase1Allocation)} PIN
          </div>
          <ChevronRight className="w-[25px] h-[25px] text-gray-900 flex-shrink-0" />
        </div>

        <div className="absolute left-[18px] right-[18px] h-px top-[99px] bg-gray-200" />

        <div className="absolute left-[18px] right-[18px] h-16 top-[22px]">
          <img
            className="absolute w-[50px] h-[50px] top-1 right-0 object-cover rounded-lg shadow-card"
            alt="AI Credit Icon"
            src={pentacleGray}
          />

          <div className="absolute left-0 right-[58px] top-0.5 bottom-0">
            <div className="relative w-full h-full flex flex-col justify-center gap-1">
              <div className="font-sf-pro font-bold text-gray-900 text-lg tracking-[0] leading-[20px] break-words text-left">
              Exclusive AI Credit Sale
              </div>
              <p className={`font-sf-pro font-medium ${aiCreditSale.className} text-xs sm:text-sm tracking-[0] leading-[16px] sm:leading-[18px] break-words text-left overflow-hidden`}>
                {creditSaleHighlight.prefix}
                {creditSaleHighlight.highlight && (
                  <span className="text-[#E15CE6]">{creditSaleHighlight.highlight}</span>
                )}
                {creditSaleHighlight.suffix}
              </p>
            </div>
          </div>
        </div>
      </div>
    </button>
  )

  // Phase2 卡片组件
  const Phase2Card = () => (
    <button 
      onClick={() => checkBinanceAndExecute(() => {
        if (aiMiningMachine.isPaid) {
          if (aiMiningMachine.isPaidAndGotScore) {
            navigate('/check-allocation')
          } else {
            navigate('/allocation-help')
          }
        } else {
          navigate('/phase2-detail')
        }
      })}
      className={`relative w-full h-[161px] transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] ${
        isPhase2Active 
          ? 'gradient-border' 
          : 'rounded-[12px] border-[0.5px] border-solid border-[#CACFDB] bg-[rgba(255,255,255,0.75)]'
      }`}
    >
      <div className={isPhase2Active ? 'gradient-border-inner' : ''}>
        <div className="flex items-center gap-3 absolute top-[110px] left-[18px] right-[18px] min-h-[35px]">
        {aiMiningMachine.isPaid ? (
            <img 
              src={iconCoin} 
              alt="Coin Icon" 
              className="w-7 h-7 rounded-lg flex-shrink-0 object-cover"
            />
          ) : (
            <img 
              src={iconAllocation} 
              alt="Allocation Icon" 
              className="w-7 h-7 rounded-lg flex-shrink-0 object-cover"
            />
          )}
          <div className="flex-1 font-sf-pro font-total text-gray-900 text-sm sm:text-base tracking-[0] leading-[18px] sm:leading-[20px] break-words overflow-hidden flex items-center">
            {aiMiningMachine.isPaid ? (
              <span className="whitespace-nowrap">
                {aiMiningMachine.isPaidAndGotScore 
                  ? (aiMiningMachine.isTaskFinished 
                      ? "This week's tasks completed"
                      : "Complete tasks to keep mining")
                  : "Activate your AI to earn"
                }
              </span>
            ) : (
              <span className="break-words">Total Allocation {formatNumberWithCommas(phaseConfig.phase2Allocation)} PIN</span>
            )}
          </div>
          {aiMiningMachine.isPaid && aiMiningMachine.unfinished_tasks > 0 && (
            <div className="flex items-center justify-center w-6 h-6 bg-[#F15555] text-white text-xs font-bold rounded-[20px] flex-shrink-0">
              {aiMiningMachine.unfinished_tasks}
            </div>
          )}
          <ChevronRight className="w-[25px] h-[25px] text-gray-900 flex-shrink-0" />
        </div>

        <div className="absolute left-[18px] right-[18px] h-px top-[99px] bg-gray-200" />

        <div className="absolute left-[18px] right-[18px] h-16 top-[22px]">
          <img
            className="absolute w-[50px] h-[50px] top-1 right-0 object-cover rounded-lg shadow-card"
            alt="Mining Machine Icon"
            src={pentacleLight}
          />

          <div className="absolute left-0 right-[58px] top-0.5 bottom-0">
            <div className="relative w-full h-full flex flex-col justify-center gap-1">
              <div className="font-sf-pro font-bold text-gray-900 text-base sm:text-lg tracking-[0] leading-[18px] sm:leading-[20px] break-words text-left">
              Exclusive AI Mining Subscription
              </div>
              <p className={`font-sf-pro font-medium ${aiMiningMachine.className} text-xs sm:text-sm tracking-[0] leading-[16px] sm:leading-[18px] break-words text-left overflow-hidden`}>
                {miningMachineHighlight.prefix}
                {miningMachineHighlight.highlight && (
                  <span className="text-[#E15CE6]">{miningMachineHighlight.highlight}</span>
                )}
                {miningMachineHighlight.suffix}
              </p>
            </div>
          </div>
        </div>
      </div>
    </button>
  )

  return (
    <div className="w-full min-h-screen">
      {/* 加载状态 */}
      {isLoading && (
        <div className="fixed inset-0 bg-white/80 flex items-center justify-center z-50">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
            <p className="text-gray-600">Loading...</p>
          </div>
        </div>
      )}
      
      {/* 错误提示 */}
      {error && (
        <div className="fixed top-4 left-4 right-4 bg-red-50 border border-red-200 rounded-lg p-3 z-40">
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}
      
      <div className="flex flex-col px-5 min-h-screen">
        {/* Header Section */}
        <div className="flex-shrink-0 pt-[30px] pb-6 relative">
          {/* Connect Wallet Button - fixed positioned overlay */}
          <WalletConnect className="absolute top-[10px] right-0 z-10" />
          
          <div className="flex flex-col gap-2.5">
            <div className="font-inter font-bold text-gray-900 text-[32px] sm:text-[35px] tracking-[0] leading-[38px] sm:leading-[42px]">
              PIN AI's
              <br />
              Exclusive Booster 
              <br/>
              Airdrop Campaign
            </div>
            <p className="font-sf-pro font-medium text-gray-600 text-sm sm:text-base tracking-[0] leading-[20px] sm:leading-[22px]">
              Exclusively via Binance Keyless Wallet
            </p>
          </div>
        </div>

        {/* TGE Details Section */}
        <div className="flex flex-col gap-[15px] flex-1 pb-6">
          {/* Live Campaign Card */}
          <div className="relative w-full h-[113px] bg-gray-900 rounded-[12px] border-[0.5px] border-solid border-[#CACFDB] flex flex-col justify-center overflow-hidden">
            <div className="flex flex-col w-full items-center">
              <div className="w-full overflow-hidden flex items-center justify-center">
                <img
                  className="w-full object-cover object-center"
                  alt="PIN AI Banner"
                  src={pianiBanner}
                />
              </div>
              <div className="relative w-full h-[50px] mt-[-1px]">
                <div className={`relative w-full h-[50px] ${pinAIBanner.className} rounded-[0px_0px_12px_12px]`}>
                  <p className="absolute inset-0 flex items-center justify-center">
                    {pinAIBanner.text}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Phase Cards - 根据当前状态动态排序 */}
          {isPhase1Active || currentPhaseState === PhaseTimeState.BEFORE_PHASE1 ? (
            <>
              <Phase1Card />
              <Phase2Card />
            </>
          ) : (
            <>
              <Phase2Card />
              <Phase1Card />
            </>
          )}
        </div>

        {/* Navigation Buttons - Fixed at bottom */}
        <div className="flex-shrink-0 h-[73px] pb-5 mb-[34px]">
          <div className="grid grid-cols-4 gap-2 sm:gap-4 h-full items-center">

            <button 
              onClick={() => navigate('/allocation-help')}
              className="flex flex-col items-center gap-0.5"
            >
              <div className="w-11 h-11 flex items-center justify-center">
                <img 
                  src={logoGuide} 
                  alt="Guide" 
                  className="w-11 h-11"
                />
              </div>
              <span className="text-[13px] sm:text-[15px] font-bold text-gray-500 text-center font-sf-pro leading-tight">
                Guide
              </span>
            </button>

            <button 
              onClick={() => navigate('/check-allocation')}
              className="flex flex-col items-center gap-0.5"
            >
              <div className="w-11 h-11 flex items-center justify-center">
                <img 
                  src={logoTokenomics} 
                  alt="Tokenomics" 
                  className="w-11 h-11"
                />
              </div>
              <span className="text-[13px] sm:text-[15px] font-bold text-gray-500 text-center font-sf-pro leading-tight">
                Tokenomics
              </span>
            </button>

            <button 
              onClick={() => window.open('https://www.pinai.io/', '_external')}
              className="flex flex-col items-center gap-0.5"
            >
              <div className="w-11 h-11 flex items-center justify-center">
                <img 
                  src={logoPinai} 
                  alt="PIN AI" 
                  className="w-11 h-11"
                />
              </div>
              <span className="text-[13px] sm:text-[15px] font-bold text-gray-500 text-center font-sf-pro leading-tight">
                PIN AI
              </span>
            </button>

            <button 
              onClick={() => window.open('https://twitter.com/pinai_io', '_external')}
              className="flex flex-col items-center gap-0.5"
            >
              <div className="w-11 h-11 flex items-center justify-center">
                <img 
                  src={logoTwitter} 
                  alt="Twitter" 
                  className="w-11 h-11"
                />
              </div>
              <span className="text-[13px] sm:text-[15px] font-bold text-gray-500 text-center font-sf-pro leading-tight">
                Twitter
              </span>
            </button>
          </div>
        </div>
      </div>
      
      {/* 开发环境下显示测试面板 import.meta.env.DEV*/}
      { import.meta.env.DEV && (
        <>
          <HomeTestPanel config={phaseConfig} setConfig={setPhaseConfig} />
        </>
      )}
       <DevConsole />
    </div>
  )
} 